import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiFolder, FiUsers } from 'react-icons/fi';
import { storageBoxApi } from '../api/storageBoxApi';
import { useStorageBox } from '../StorageBoxContext';
import StorageBoxSidebarSection, {
  readSidebarSectionExpanded,
  writeSidebarSectionExpanded,
} from './StorageBoxSidebarSection';
import StorageBoxSharedManageModal from './StorageBoxSharedManageModal';
import { STORAGE_BOX_ROOT_LABEL } from '../constants';
import { PeopleListSkeleton } from '../../layout/ReusableComponent/Skeleton/RouteSkeletons';
import { useStorageBoxSidebarDrawer } from '../StorageBoxSidebarDrawerContext';

const SECTION_KEY = 'sb-sidebar-shared-expanded';

function groupSharesByOwner(shares) {
  const map = new Map();
  for (const s of shares) {
    const key = s.ownerUserId || s.ownerName || 'unknown';
    if (!map.has(key)) {
      map.set(key, { ownerUserId: s.ownerUserId, ownerName: s.ownerName || 'Unknown', shares: [] });
    }
    map.get(key).shares.push(s);
  }
  return Array.from(map.values()).sort((a, b) => a.ownerName.localeCompare(b.ownerName));
}

export default function StorageBoxSidebarShared() {
  const {
    readOnly,
    openSharedItem,
    browseMode,
    activeSharedShareId,
    vaultScope,
    lockVaultAccessActive,
    goToRoot,
    notify,
  } = useStorageBox();
  const inLockRealm = vaultScope === 'lock';
  const lockUnlocked = lockVaultAccessActive;
  const [expanded, setExpanded] = useState(() => readSidebarSectionExpanded(SECTION_KEY, true));
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOwners, setExpandedOwners] = useState(() => new Set());
  const [manageOpen, setManageOpen] = useState(false);
  const closeDrawer = useStorageBoxSidebarDrawer();

  const load = useCallback(() => {
    if (readOnly) return;
    if (inLockRealm && !lockUnlocked) {
      setShares([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    storageBoxApi
      .listSharedWithMe()
      .then((r) => setShares(r.shares || []))
      .catch(() => setShares([]))
      .finally(() => setLoading(false));
  }, [readOnly, inLockRealm, lockUnlocked]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener('storagebox-shared-list-changed', onRefresh);
    return () => window.removeEventListener('storagebox-shared-list-changed', onRefresh);
  }, [load]);

  const visibleShares = useMemo(
    () => shares.filter((s) => (inLockRealm ? Boolean(s.requiresLockPin) : !s.requiresLockPin)),
    [shares, inLockRealm]
  );

  const ownerGroups = useMemo(() => groupSharesByOwner(visibleShares), [visibleShares]);

  useEffect(() => {
    if (!ownerGroups.length) return;
    setExpandedOwners((prev) => {
      if (prev.size) return prev;
      const next = new Set(ownerGroups.map((g) => g.ownerUserId || g.ownerName));
      return next;
    });
  }, [ownerGroups]);

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      writeSidebarSectionExpanded(SECTION_KEY, next);
      return next;
    });
  };

  const toggleOwner = (ownerKey) => {
    setExpandedOwners((prev) => {
      const next = new Set(prev);
      if (next.has(ownerKey)) next.delete(ownerKey);
      else next.add(ownerKey);
      return next;
    });
  };

  const sectionTitle = inLockRealm ? 'Shared in Lock folder' : `Shared in ${STORAGE_BOX_ROOT_LABEL}`;
  const shareScope = inLockRealm ? 'lock' : 'drive';

  const handleShareListChange = useCallback(
    ({ clearedShareId }) => {
      if (clearedShareId && clearedShareId === activeSharedShareId) {
        goToRoot();
      }
    },
    [activeSharedShareId, goToRoot]
  );

  if (readOnly) return null;

  if (inLockRealm && !lockUnlocked) {
    return null;
  }

  return (
    <>
      <StorageBoxSidebarSection
        title={sectionTitle}
        expanded={expanded}
        onToggle={toggleExpanded}
        className="sb-drive-shared-nav"
      >
        <button
          type="button"
          className="sb-shared-manage-trigger"
          onClick={() => {
            setManageOpen(true);
            closeDrawer();
          }}
        >
          Manage sharing
        </button>
        {loading ? <PeopleListSkeleton rows={2} className="sb-drive-shared-skeleton" /> : null}
        {!loading && !ownerGroups.length ? (
          <p className="sb-drive-sidebar-section__hint">
            {inLockRealm
              ? 'Nothing shared with you in Lock folder yet.'
              : `Nothing shared with you in ${STORAGE_BOX_ROOT_LABEL} yet.`}
          </p>
        ) : null}
        <ul className="sb-drive-shared-list">
          {ownerGroups.map((group) => {
            const ownerKey = group.ownerUserId || group.ownerName;
            const ownerOpen = expandedOwners.has(ownerKey);
            return (
              <li key={ownerKey} className="sb-drive-shared-owner">
                <button
                  type="button"
                  className="sb-drive-nav-item sb-drive-nav-item--owner"
                  onClick={() => toggleOwner(ownerKey)}
                  aria-expanded={ownerOpen}
                >
                  {ownerOpen ? <FiChevronDown aria-hidden /> : <FiChevronRight aria-hidden />}
                  <FiFolder aria-hidden />
                  <span className="sb-drive-nav-item__label">{group.ownerName}</span>
                </button>
                {ownerOpen ? (
                  <ul className="sb-drive-shared-list sb-drive-shared-list--nested">
                    {group.shares.map((s) => {
                      const active =
                        (browseMode === 'shared' || browseMode === 'lock') && activeSharedShareId === s.shareId;
                      return (
                        <li key={s.shareId}>
                          <button
                            type="button"
                            className={`sb-drive-nav-item sb-drive-nav-item--nested ${active ? 'sb-drive-nav-item--active' : ''}`}
                            onClick={() => {
                              openSharedItem(s);
                              closeDrawer();
                            }}
                            title={s.name}
                          >
                            <FiUsers aria-hidden />
                            <span className="sb-drive-nav-item__label">{s.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </StorageBoxSidebarSection>

      <StorageBoxSharedManageModal
        isOpen={manageOpen}
        onClose={() => setManageOpen(false)}
        scope={shareScope}
        onNotify={notify}
        activeSharedShareId={activeSharedShareId}
        onAfterChange={handleShareListChange}
      />
    </>
  );
}
